<template>
  <div
    class="message-template__panel absolute right-0 z-10 h-full w-[var(--contact-panel-width)] overflow-y-auto border-l bg-white text-sm shadow-xl dark:border-gray-800/50 dark:bg-gray-900"
  >
    <div class="flex flex-row flex-nowrap items-center justify-end px-2 pt-2">
      <woot-button
        class="close-button"
        variant="clear"
        color-scheme="secondary"
        icon="dismiss"
        @click="close"
      />
    </div>

    <div
      v-if="messageTemplate"
      class="message-template__panel__body -mt-2 flex flex-col gap-4 px-4"
    >
      <message-template-info-actions
        :message-template="messageTemplate"
        :message-template-type="messageTemplateType"
        :is-deleting="isDeleting"
        @clone-template="redirectToBuilder"
        @delete-template="deleteTemplate"
        @fetch-message-templates="fetchMessageTemplates"
        @fetch-message-template="fetchMessageTemplate"
      />

      <div class="flex flex-col flex-nowrap gap-3">
        <language-bar
          :languages="languages"
          :current-language="currentLanguage"
          :translations="messageTemplate.translations"
          :has-additional-text="false"
          :custom-mark-color="currentTranslationStatusOptionColor"
          :has-color-mark="isWhatsApp"
          :has-add-button="!isDeleting && !shouldHideEditActions"
          :has-popover="false"
          @change="selectCurrentLanguage"
          @handle-add-button-click="redirectToBuilder(builderMode.EDIT)"
        />

        <template-message-preview
          class="message-template__panel__body__preview"
          :current-translation="currentTranslation"
          :template-type="messageTemplateType"
        />

        <div class="flex flex-row flex-nowrap gap-2">
          <woot-button
            v-if="!shouldHideEditActions"
            class="message-template__action-button"
            variant="smooth"
            color-scheme="secondary"
            :icon="isViewOnly ? 'eye-show' : 'edit'"
            size="small"
            :is-disabled="isEditButtonDisabled"
            @click="redirectToBuilder(builderMode.EDIT)"
          >
            {{
              $t(
                isViewOnly
                  ? 'CHATLYN_MESSAGE_TEMPLATE.BUTTON.VIEW_LANGUAGE'
                  : 'CHATLYN_MESSAGE_TEMPLATE.BUTTON.EDIT_LANGUAGE',
                {
                  LANGUAGE: currentLanguage.toUpperCase(),
                }
              )
            }}
          </woot-button>

          <woot-button
            v-if="hasDeleteTranslationLanguageButton"
            class="message-template__action-button"
            variant="smooth"
            color-scheme="alert"
            icon="delete"
            size="small"
            @click="toggleShowDeleteLanguageModal"
          >
            {{
              $t('CHATLYN_MESSAGE_TEMPLATE.BUTTON.DELETE_LANGUAGE', {
                LANGUAGE: currentLanguage.toUpperCase(),
              })
            }}
          </woot-button>
        </div>

        <div class="message-template__info flex flex-col gap-1 py-4">
          <div>
            <span>
              <fluent-icon icon="whatsapp" :size="16" />
            </span>

            <span>
              {{
                $t(
                  `CHATLYN_MESSAGE_TEMPLATE.LIST.INBOX_TYPE.${messageTemplateType.toUpperCase()}`
                )
              }}
            </span>
          </div>

          <div v-if="messageTemplateStatus">
            <span>
              <fluent-icon icon="stack" :size="16" />
            </span>

            <span>
              {{
                `${messageTemplateStatus} ${$t(
                  'CHATLYN_MESSAGE_TEMPLATE.TEMPLATE'
                )}`
              }}
            </span>
          </div>

          <div>
            <span>
              <fluent-icon icon="calendar-empty" :size="16" />
            </span>

            <span>
              {{ currentTranslationCreatedAt }}
            </span>
          </div>

          <div v-if="isWhatsApp" class="flex items-center gap-2">
            <span
              class="status-indicator mx-0.5"
              :style="{
                'background-color': currentTranslationStatusOptionColor,
              }"
            />
            <span>
              {{ currentTranslationStatusOptionText }}
            </span>
          </div>

          <div v-if="statusReason" class="-mt-1 ps-6">
            {{
              $t('CHATLYN_MESSAGE_TEMPLATE.REJECT_MESSAGE', {
                REASON: statusReason,
              })
            }}
          </div>
        </div>
      </div>
    </div>

    <teleport to="#chatlyn-app-target">
      <div>
        <woot-delete-modal
          :show.sync="showDeleteLanguageModal"
          :title="
            $t('CHATLYN_MESSAGE_TEMPLATE.REMOVE_TRANSLATION_MODAL.TITLE', {
              LANGUAGE: currentLanguage.toUpperCase(),
            })
          "
          :message="
            $t('CHATLYN_MESSAGE_TEMPLATE.REMOVE_TRANSLATION_MODAL.MESSAGE')
          "
          :confirm-text="
            $t('CHATLYN_MESSAGE_TEMPLATE.REMOVE_TRANSLATION_MODAL.ACCEPT')
          "
          :reject-text="
            $t('CHATLYN_MESSAGE_TEMPLATE.REMOVE_TRANSLATION_MODAL.DECLINE')
          "
          :on-confirm="deleteLanguage"
          :on-close="toggleShowDeleteLanguageModal"
          :is-loading="isDeleting"
          :close-on-backdrop-click="false"
        />
      </div>
    </teleport>

    <chatlyn-spinner :is-visible="isLoading" />
  </div>
</template>

<script>
// Libs //
import { mapGetters } from 'vuex';
import { format } from 'date-fns';
import Teleport from 'vue2-teleport';

// Mixins //
import templatesMixin from '../../../../mixins/templatesMixin';

// Components //
import TemplateMessagePreview from './TemplateMessagePreview.vue';
import MessageTemplateInfoActions from './MessageTemplateInfoActions.vue';
import LanguageBar from './LanguageBar.vue';
import ChatlynSpinner from '../../../../../shared/components/ChatlynSpinner.vue';

// Constants //
import {
  MESSAGE_TEMPLATES_CHANNEL_TYPE,
  MESSAGE_TEMPLATES_STATUSES_OPTION,
  TRANSLATION_STATUS,
  TRANSLATION_STATUS_EDITABLE,
  TEMPLATE_BUILDER_MODE,
} from '../constants';

export default {
  name: 'MessageTemplateInfoPanel',

  components: {
    Teleport,
    TemplateMessagePreview,
    MessageTemplateInfoActions,
    LanguageBar,
    ChatlynSpinner,
  },

  mixins: [templatesMixin],

  props: {
    selectedTranslationLanguage: {
      type: String,
      default: '',
    },

    messageTemplateType: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      builderMode: TEMPLATE_BUILDER_MODE,
      showDeleteLanguageModal: false,
    };
  },

  computed: {
    ...mapGetters({
      messageTemplate: 'messageTemplates/getMessageTemplate',
      messageTemplatesUIFlags: 'messageTemplates/getUIFlags',
      accountId: 'getCurrentAccountId',
      uiFlags: 'messageTemplates/getUIFlags',
      isPhoenix: 'accounts/isPhoenix',
    }),

    currentLanguage: {
      get() {
        return this.selectedTranslationLanguage;
      },

      set(newValue) {
        this.$emit('change-selected-translation-language', newValue);
      },
    },

    languages() {
      return this.messageTemplate.translations.map((translation) => ({
        name: translation.language,
      }));
    },

    messageTemplateStatus() {
      if (!this.messageTemplate.category) {
        return '';
      }

      return this.$t(
        `CHATLYN_MESSAGE_TEMPLATE.CATEGORY.${this.messageTemplate.category}`
      );
    },

    currentTranslation() {
      return (
        this.messageTemplate.translations.find(
          (translation) => translation.language === this.currentLanguage
        ) ?? {}
      );
    },

    currentTranslationIndex() {
      return this.messageTemplate.translations.findIndex(
        (translation) => translation.language === this.currentLanguage
      );
    },

    currentTranslationCreatedAt() {
      if (!this.currentTranslation?.createdAt) {
        return '';
      }

      return `${this.$t('CHATLYN_MESSAGE_TEMPLATE.PANEL.CREATED_ON')} ${format(
        new Date(this.currentTranslation.createdAt),
        `MMM dd, HH:mm`
      )}`;
    },

    currentTranslationStatus() {
      return (
        this.currentTranslation?.status ||
        this.messageTemplate?.status ||
        ''
      ).toLowerCase();
    },

    currentTranslationStatusOption() {
      return MESSAGE_TEMPLATES_STATUSES_OPTION[this.currentTranslationStatus];
    },

    currentTranslationStatusOptionColor() {
      if (
        !this.currentTranslationStatusOption ||
        !this.currentTranslationStatusOption.color
      ) {
        return '';
      }

      return this.currentTranslationStatusOption.color;
    },

    currentTranslationStatusOptionText() {
      if (
        !this.currentTranslationStatusOption ||
        !this.currentTranslationStatusOption.title
      ) {
        return '';
      }

      return this.$t(this.currentTranslationStatusOption.title);
    },
    isSMS() {
      return MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS === this.messageTemplateType;
    },
    isEditableStatus() {
      return TRANSLATION_STATUS_EDITABLE.includes(
        this.currentTranslationStatus
      );
    },

    isViewOnly() {
      // Locked WhatsApp statuses (approved/pending/submitted) open the builder
      // in its read-only mode, so the action becomes "View" instead of "Edit".
      return this.isWhatsApp && !this.isEditableStatus;
    },

    isEditButtonDisabled() {
      return this.isDeleting;
    },

    hasDeleteTranslationLanguageButton() {
      if (!Object.keys(this.currentTranslation).length) {
        return false;
      }
      const notFirst = this.currentTranslationIndex !== 0;

      // For WhatsApp: only allow deleting DRAFT translations
      // For SMS: allow deleting any translation (except first)
      if (this.isWhatsApp) {
        return (
          notFirst &&
          this.currentTranslationStatus === TRANSLATION_STATUS.DRAFT &&
          !this.isDeleting
        );
      }

      return notFirst && this.isSMS && !this.isDeleting;
    },

    isDeleting() {
      return (
        // eslint-disable-next-line no-underscore-dangle
        this.isTemplateDeleting(this.messageTemplate?._id)
      );
    },

    statusReason() {
      return this.currentTranslationStatus === TRANSLATION_STATUS.REJECTED
        ? this.currentTranslation?.rejectedReason ||
            this.messageTemplate?.rejectedReason ||
            ''
        : '';
    },

    isWhatsApp() {
      return (
        MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP === this.messageTemplateType
      );
    },

    isLoading() {
      return this.uiFlags.isFetching || this.uiFlags.isTemplateFetching;
    },

    hasAnyRejectedTranslation() {
      return this.messageTemplate?.translations?.some(
        (translation) =>
          translation.status?.toLowerCase() === TRANSLATION_STATUS.REJECTED
      );
    },

    shouldHideEditActions() {
      return (
        !this.isPhoenix && this.isWhatsApp && this.hasAnyRejectedTranslation
      );
    },
  },

  watch: {
    messageTemplate: {
      handler(newValue) {
        if (!newValue) {
          return;
        }

        if (
          this.currentLanguage &&
          newValue.translations.some(
            (translation) => translation.language === this.currentLanguage
          )
        ) {
          return;
        }

        this.currentLanguage = newValue.translations[0].language;
      },
      deep: true,
    },
  },

  methods: {
    toggleShowDeleteLanguageModal() {
      this.showDeleteLanguageModal = !this.showDeleteLanguageModal;
    },

    close() {
      this.$emit('close');
    },

    async deleteTemplate() {
      this.$emit('delete-template');
    },

    selectCurrentLanguage(newValue) {
      this.currentLanguage = newValue;
    },

    redirectToBuilder(mode) {
      this.$router.push({
        name: 'template_builder',
        params: {
          type: this.messageTemplateType,
          mode,
        },
        query: {
          // eslint-disable-next-line no-underscore-dangle
          templateId: this.messageTemplate._id,
        },
      });
    },

    fetchMessageTemplates() {
      this.$emit('fetch-message-templates');
    },

    fetchMessageTemplate(id) {
      this.$emit('fetch-message-template', id);
    },

    async deleteLanguage() {
      if (!this.currentLanguage) {
        return;
      }

      const matchedTranslation = this.messageTemplate.translations.find(
        (transition) => transition.language === this.currentLanguage
      );

      try {
        let deleteResponse;

        switch (this.messageTemplateType) {
          case MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS:
            deleteResponse = await this.$store.dispatch(
              'messageTemplates/deleteSmsMessageTemplateTranslation',
              {
                templateId: this.messageTemplate._id,
                language: this.currentLanguage,
                translationId: matchedTranslation._id,
                accountId: this.accountId,
              }
            );
            break;

          case MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP:
            if (matchedTranslation.status) {
              deleteResponse = await this.$store.dispatch(
                'messageTemplates/deleteMessageTemplateTranslation',
                {
                  // eslint-disable-next-line no-underscore-dangle
                  templateId: this.messageTemplate._id,
                  language: this.currentLanguage,
                  translationId: matchedTranslation._id,
                  status: matchedTranslation.status,
                  // Only pass Phoenix params for Phoenix accounts
                  ...(this.isPhoenix && {
                    accountId: this.accountId,
                    wabaId: this.messageTemplate.wabaId,
                    phoneNumber: this.messageTemplate.phoneNumber,
                  }),
                }
              );
            }
            break;

          default:
            break;
        }

        if (!deleteResponse) {
          throw new Error();
        }

        const newTranslations = structuredClone(
          this.messageTemplate.translations.filter(
            (transition) => transition.language !== this.currentLanguage
          )
        );

        this.messageTemplate.translations = newTranslations;

        this.toggleShowDeleteLanguageModal();
      } catch (error) {
        //
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.message-template__panel {
  .message-template__panel__body {
    .message-template__action-button {
      @apply flex flex-1 items-center justify-center;

      ::v-deep .button__content {
        @apply w-auto;
      }
    }

    .message-template__info {
      > div {
        @apply flex items-center gap-2;
      }
    }
  }
}
</style>
